'use client'

import { useState, useRef, useEffect } from 'react'
import { useFirebaseService } from '@/hooks/useFirebaseService'
import { Camera, Upload, Zap, TrendingUp, Loader2, CheckCircle, ChevronDown, ChevronUp, Image as ImageIcon, X, AlertTriangle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Dialog, DialogContent } from '@/components/ui/dialog'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import { cn } from '@/lib/utils'

interface TickerExtractorProps {
  mode?: 'embedded' | 'dialog'
  onTickersSelected?: (tickers: string[], listName: string) => void
  onTickersAdded?: (listId: string) => void
  onClose?: () => void
  existingTickers?: string[]
  className?: string
  isCompactView?: boolean
  userId?: string
}

export function TickerExtractor({
  mode = 'embedded',
  onTickersSelected,
  onTickersAdded,
  onClose,
  existingTickers = [],
  className,
  isCompactView = false,
  userId
}: TickerExtractorProps) {
  const { getTickersFromImage, addUserList, addItemToWatchlist } = useFirebaseService()
  const fileInputRef = useRef<HTMLInputElement>(null)

  const [isExtractingTickers, setIsExtractingTickers] = useState(false)
  const [extractionError, setExtractionError] = useState<string | null>(null)
  const [extractedTickers, setExtractedTickers] = useState<string[]>([])
  const [selectedTickers, setSelectedTickers] = useState<string[]>([])
  const [listName, setListName] = useState<string>('')
  const [uploadedImage, setUploadedImage] = useState<string | null>(null)
  const [isImageExpanded, setIsImageExpanded] = useState(false)
  const [isFullScreenImage, setIsFullScreenImage] = useState(false)
  const [isVisible, setIsVisible] = useState(false)
  const [activeOption, setActiveOption] = useState<'upload' | 'camera' | null>(null)

  // Generate a random watchlist name
  const generateRandomName = () => {
    const adjectives = ['Active', 'Dynamic', 'Strategic', 'Growth', 'Value', 'Tech', 'Global', 'Market', 'Trending', 'Focus']
    const nouns = ['Portfolio', 'Picks', 'Stocks', 'Watchlist', 'Investments', 'Tracker', 'Selection', 'Movers', 'Leaders', 'Opportunities']

    const randomAdjective = adjectives[Math.floor(Math.random() * adjectives.length)]
    const randomNoun = nouns[Math.floor(Math.random() * nouns.length)]
    const randomNumber = Math.floor(Math.random() * 100)

    return `${randomAdjective} ${randomNoun} ${randomNumber}`
  }

  // Initialize with a random name and trigger animations
  useEffect(() => {
    if (!listName) {
      setListName(generateRandomName())
    }
    // Trigger staggered animation
    setTimeout(() => setIsVisible(true), 50)
  }, [])

  const resetState = () => {
    setExtractedTickers([])
    setSelectedTickers([])
    setExtractionError(null)
    setListName(generateRandomName())
    setUploadedImage(null)
    setIsImageExpanded(false)
    setIsFullScreenImage(false)
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    setIsExtractingTickers(true)
    setExtractionError(null)
    setExtractedTickers([])
    setSelectedTickers([])

    try {
      const reader = new FileReader()
      reader.readAsDataURL(file)

      await new Promise<void>((resolve, reject) => {
        reader.onload = async () => {
          try {
            const base64String = reader.result as string
            setUploadedImage(base64String)

            const base64Data = base64String.split(',')[1]

            const tickers = await getTickersFromImage(base64Data)
            setExtractedTickers(tickers)

            // Auto-select tickers that aren't in the existing list
            const newTickers = tickers.filter(ticker => !existingTickers.includes(ticker))
            setSelectedTickers(newTickers)

            if (tickers.length === 0) {
              setExtractionError('Unable to find any relevant information')
            } else if (newTickers.length === 0 && tickers.length > 0) {
              setExtractionError('Tickers already in your watchlist')
            }
            resolve()
          } catch (error: any) {
            console.error('Error extracting tickers:', error)
            setExtractionError('Unable to find any relevant information')
            reject(error)
          } finally {
            setIsExtractingTickers(false)
          }
        }

        reader.onerror = (error) => {
          console.error('Error reading file:', error)
          setIsExtractingTickers(false)
          setExtractionError('Unable to find any relevant information')
          reject(error)
        }
      })
    } catch (error: any) {
      setIsExtractingTickers(false)
      setExtractionError('Unable to find any relevant information')
      console.error('Error in handleFileChange:', error)
    }
  }

  const handleToggleTicker = (ticker: string) => {
    if (existingTickers.includes(ticker)) return

    setSelectedTickers(prev =>
      prev.includes(ticker)
        ? prev.filter(t => t !== ticker)
        : [...prev, ticker]
    )
    if (extractionError === 'All extracted tickers are already in your list') {
      setExtractionError(null);
    }
  }

  const handleQuickTickerExtraction = async (tickers: string[], listName: string) => {
    if (!userId || tickers.length === 0) return;

    try {
      const newListId = await addUserList(userId, listName);

      for (const ticker of tickers) {
        try {
          const response = await fetch(`/api/finance/${ticker}`);
          const quoteData = await response.json();

          if (quoteData && quoteData.symbol) {
            await addItemToWatchlist(newListId, quoteData);
          }
        } catch (err) {
          console.error(`Error adding ticker ${ticker}:`, err);
        }
      }

      if (onTickersAdded) {
        onTickersAdded(newListId);
      }
    } catch (err) {
      console.error('Error creating watchlist:', err);
    }
  };

  const handleConfirmSelection = () => {
    if (selectedTickers.length > 0) {
      if (userId) {
        handleQuickTickerExtraction(selectedTickers, listName);
      } else if (onTickersSelected) {
        onTickersSelected(selectedTickers, listName);
      }
      resetState();
    }
  }

  // Render functions for different states
  const renderUploadOptions = () => {
    // Full screen image view
    if (isFullScreenImage && uploadedImage) {
      return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-90">
          <div className="relative w-full h-full flex items-center justify-center">
            <Button
              variant="ghost"
              size="icon"
              className="absolute top-4 right-4 text-white hover:bg-gray-800 z-10"
              onClick={() => setIsFullScreenImage(false)}
            >
              <X className="h-6 w-6" />
            </Button>
            <img
              src={uploadedImage}
              alt="Uploaded ticker image"
              className="max-w-full max-h-full object-contain p-4"
            />
          </div>
        </div>
      );
    }

    // Collapsible image preview when tickers are extracted
    if (uploadedImage && extractedTickers.length > 0) {
      return (
        <Collapsible
          open={isImageExpanded}
          onOpenChange={setIsImageExpanded}
          className="w-full border border-[#30363d]/50 rounded-xl overflow-hidden mb-6"
        >
          <div className="flex items-center justify-between p-3 bg-[#161b22]">
            <div className="flex items-center gap-2">
              <ImageIcon className="h-4 w-4 text-[#8b949e]" />
              <span className="text-sm font-medium text-[#e6edf3]">Uploaded Image</span>
            </div>
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="sm"
                className="p-1 h-auto hover:bg-[#30363d]"
                onClick={() => setIsFullScreenImage(true)}
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-[#8b949e]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5v-4m0 4h-4m4 0l-5-5" />
                </svg>
              </Button>
              <CollapsibleTrigger asChild>
                <Button variant="ghost" size="sm" className="p-1 h-auto hover:bg-[#30363d]">
                  {isImageExpanded ? (
                    <ChevronUp className="h-4 w-4 text-[#8b949e]" />
                  ) : (
                    <ChevronDown className="h-4 w-4 text-[#8b949e]" />
                  )}
                </Button>
              </CollapsibleTrigger>
            </div>
          </div>
          <CollapsibleContent>
            <div className="p-3 bg-[#0d1117] flex justify-center">
              <img
                src={uploadedImage}
                alt="Uploaded ticker image"
                className="max-h-48 object-contain rounded-md cursor-pointer"
                onClick={() => setIsFullScreenImage(true)}
              />
            </div>
          </CollapsibleContent>
        </Collapsible>
      );
    }

    // Initial upload/camera options with Magazine Editorial design
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {/* Upload card */}
        <button
          onClick={() => fileInputRef.current?.click()}
          onMouseEnter={() => setActiveOption('upload')}
          onMouseLeave={() => setActiveOption(null)}
          className={cn(
            "relative group p-6 rounded-2xl border-2 transition-all duration-500 text-left overflow-hidden",
            isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8",
            activeOption === 'upload'
              ? "border-[#d4a853] bg-[#d4a853]/5 shadow-xl shadow-[#d4a853]/20"
              : "border-[#30363d] bg-[#161b22] hover:border-[#30363d]/80"
          )}
          style={{ transitionDelay: isVisible ? '200ms' : '0ms' }}
        >
          <div className={cn(
            "absolute inset-0 bg-gradient-to-br from-[#d4a853]/10 via-transparent to-transparent transition-opacity duration-500",
            activeOption === 'upload' ? "opacity-100" : "opacity-0"
          )} />

          <div className="relative z-10">
            <div className="flex items-start justify-between mb-4">
              <div className={cn(
                "p-3 rounded-xl transition-all duration-500",
                activeOption === 'upload'
                  ? "bg-[#d4a853] scale-110"
                  : "bg-[#1c2128]"
              )}>
                <Upload className={cn(
                  "w-6 h-6 transition-colors duration-500",
                  activeOption === 'upload' ? "text-[#0d1117]" : "text-[#d4a853]"
                )} />
              </div>
              <div className={cn(
                "transition-all duration-500",
                activeOption === 'upload' ? "translate-x-0 opacity-100" : "translate-x-2 opacity-0"
              )}>
                <Zap className="w-5 h-5 text-emerald-400" />
              </div>
            </div>

            <h3 className="text-xl font-bold text-[#e6edf3] mb-2">
              Upload Image
            </h3>
            <p className="text-sm text-[#8b949e] leading-relaxed">
              Browse files or drag & drop your portfolio screenshot
            </p>

            <div className={cn(
              "mt-4 h-1 bg-gradient-to-r from-[#d4a853] to-emerald-500 rounded-full transition-all duration-500 origin-left",
              activeOption === 'upload' ? "scale-x-100" : "scale-x-0"
            )} />
          </div>
        </button>

        {/* Camera card */}
        <button
          onClick={() => fileInputRef.current?.click()}
          onMouseEnter={() => setActiveOption('camera')}
          onMouseLeave={() => setActiveOption(null)}
          className={cn(
            "relative group p-6 rounded-2xl border-2 transition-all duration-500 text-left overflow-hidden",
            isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8",
            activeOption === 'camera'
              ? "border-emerald-500 bg-emerald-500/5 shadow-xl shadow-emerald-500/20"
              : "border-[#30363d] bg-[#161b22] hover:border-[#30363d]/80"
          )}
          style={{ transitionDelay: isVisible ? '300ms' : '0ms' }}
        >
          <div className={cn(
            "absolute inset-0 bg-gradient-to-br from-emerald-500/10 via-transparent to-transparent transition-opacity duration-500",
            activeOption === 'camera' ? "opacity-100" : "opacity-0"
          )} />

          <div className="relative z-10">
            <div className="flex items-start justify-between mb-4">
              <div className={cn(
                "p-3 rounded-xl transition-all duration-500",
                activeOption === 'camera'
                  ? "bg-emerald-500 scale-110"
                  : "bg-[#1c2128]"
              )}>
                <Camera className={cn(
                  "w-6 h-6 transition-colors duration-500",
                  activeOption === 'camera' ? "text-[#0d1117]" : "text-emerald-400"
                )} />
              </div>
              <div className={cn(
                "transition-all duration-500",
                activeOption === 'camera' ? "translate-x-0 opacity-100" : "translate-x-2 opacity-0"
              )}>
                <TrendingUp className="w-5 h-5 text-[#d4a853]" />
              </div>
            </div>

            <h3 className="text-xl font-bold text-[#e6edf3] mb-2">
              Take Photo
            </h3>
            <p className="text-sm text-[#8b949e] leading-relaxed">
              Snap a picture of your portfolio screen right now
            </p>

            <div className={cn(
              "mt-4 h-1 bg-gradient-to-r from-emerald-500 to-[#d4a853] rounded-full transition-all duration-500 origin-left",
              activeOption === 'camera' ? "scale-x-100" : "scale-x-0"
            )} />
          </div>
        </button>

        {/* Hidden file input */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          capture="environment"
          onChange={handleFileChange}
          className="hidden"
        />
      </div>
    );
  };

  const renderExtractionStatus = () => {
    if (isExtractingTickers) {
      return (
        <div className="mt-6 flex items-center justify-center p-4 bg-[#161b22] rounded-lg border border-[#30363d]/50" aria-live="polite">
          <Loader2 className="h-6 w-6 animate-spin text-[#d4a853]" />
          <span className="ml-3 text-sm font-medium text-[#e6edf3]">
            Extracting tickers...
          </span>
        </div>
      );
    }
    if (extractionError) {
      const isRelevantInfoError = extractionError === 'Unable to find any relevant information';
      return (
        <div className="mt-4 flex items-center justify-center text-sm text-[#8b949e] p-3 rounded-lg bg-[#161b22] border border-[#30363d]/50" role="alert">
          {isRelevantInfoError && (
            <AlertTriangle className="h-4 w-4 text-amber-500 mr-2 flex-shrink-0" />
          )}
          <span className="font-medium">{extractionError}</span>
        </div>
      );
    }
    return null;
  };

  const renderTickerSelection = () => {
    if (extractedTickers.length > 0 && !isExtractingTickers) {
      return (
        <div className="mt-6 space-y-3">
          <h4 className="text-sm font-medium text-[#8b949e] uppercase tracking-wider">Select Tickers:</h4>
          <ScrollArea className="h-[200px] rounded-xl border border-[#30363d]/50 bg-[#161b22]">
            <div className="p-4 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
              {extractedTickers.map((ticker) => {
                const isSelected = selectedTickers.includes(ticker)
                const isExisting = existingTickers.includes(ticker)
                return (
                  <div
                    key={ticker}
                    onClick={() => !isExisting && handleToggleTicker(ticker)}
                    onKeyDown={(e) => !isExisting && (e.key === 'Enter' || e.key === ' ') && handleToggleTicker(ticker)}
                    role="button"
                    tabIndex={isExisting ? -1 : 0}
                    aria-pressed={isSelected}
                    className={cn(
                      "flex items-start p-3 rounded-lg transition-all duration-300",
                      !isExisting && "hover:bg-[#30363d]/30 cursor-pointer",
                      isSelected && !isExisting && "bg-[#d4a853]/10 ring-2 ring-[#d4a853]/30",
                      isExisting && "opacity-60 cursor-not-allowed",
                      "bg-[#0d1117] border border-[#30363d]/50"
                    )}
                  >
                    {isSelected && !isExisting ? (
                      <CheckCircle className="mr-2 h-5 w-5 text-emerald-500 mt-0.5 flex-shrink-0" />
                    ) : (
                      <div className="w-5 h-5 mr-2 flex-shrink-0 rounded-full border-2 border-[#30363d] mt-0.5"></div>
                    )}
                    <span className="text-sm font-semibold text-[#e6edf3]">{ticker}</span>
                    {isExisting && (
                      <span className="text-xs text-[#8b949e] ml-auto flex-shrink-0">(exists)</span>
                    )}
                  </div>
                );
              })}
            </div>
          </ScrollArea>
        </div>
      );
    }
    return null;
  };

  const renderListNameInput = () => {
    if (!extractedTickers.length) return null;

    return (
      <div className="mt-6">
        <div className="flex items-center gap-2">
          <div className="flex-1">
            <label htmlFor="list-name" className="block text-sm font-medium text-[#8b949e] mb-2 uppercase tracking-wider">
              Watchlist Name
            </label>
            <Input
              id="list-name"
              value={listName}
              onChange={(e) => setListName(e.target.value)}
              className="w-full bg-[#161b22] border-[#30363d] text-[#e6edf3] placeholder:text-[#8b949e] transition-colors focus:border-[#d4a853] h-11 rounded-lg"
              placeholder="Enter watchlist name"
            />
          </div>
          <div className="mt-7">
            <Button
              type="button"
              variant="outline"
              onClick={() => setListName(generateRandomName())}
              className="h-11 bg-[#161b22] hover:bg-[#30363d] border-[#30363d] text-[#8b949e] hover:text-[#e6edf3] transition-colors text-xs uppercase font-bold tracking-wider px-4"
            >
              Generate
            </Button>
          </div>
        </div>
      </div>
    );
  };

  const renderConfirmButton = () => {
    if (selectedTickers.length === 0 || !extractedTickers.length) return null;

    return (
      <div className="mt-6">
        <Button
          onClick={handleConfirmSelection}
          className="w-full bg-gradient-to-r from-[#d4a853] to-emerald-500 hover:from-[#d4a853]/90 hover:to-emerald-500/90 text-[#0d1117] font-bold h-12 text-base rounded-lg shadow-lg shadow-[#d4a853]/20 transition-all duration-200 flex items-center justify-center"
        >
          Add {selectedTickers.length} Ticker{selectedTickers.length !== 1 ? 's' : ''}
        </Button>
      </div>
    );
  };

  // Stats footer (only show in initial state)
  const renderStats = () => {
    if (extractedTickers.length > 0 || isExtractingTickers) return null;

    return (
      <div className={cn(
        "mt-8 pt-6 border-t border-[#30363d]/50 grid grid-cols-3 gap-6 transition-all duration-700 ease-out",
        isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
      )} style={{ transitionDelay: isVisible ? '400ms' : '0ms' }}>
        {[
          { label: 'Accuracy', value: '99.8%' },
          { label: 'Avg. Time', value: '<3s' },
          { label: 'Formats', value: 'All' }
        ].map((stat, idx) => (
          <div key={idx}>
            <div className="text-2xl font-bold text-[#d4a853] mb-1">
              {stat.value}
            </div>
            <div className="text-xs text-[#8b949e] uppercase tracking-wide">
              {stat.label}
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderInnerContent = () => (
    <div className="space-y-6">
      {renderUploadOptions()}
      {renderExtractionStatus()}
      {renderTickerSelection()}
      {renderListNameInput()}
      {renderConfirmButton()}
      {renderStats()}
    </div>
  );

  // Render based on mode
  if (mode === 'dialog') {
    return (
      <DialogContent className="sm:max-w-[650px] p-0 border-0 bg-transparent shadow-2xl overflow-hidden [&>button]:hidden">
        <div className="relative rounded-3xl bg-[#0d1117] overflow-hidden border border-[#30363d]/50">

          {/* Animated diagonal accent bars */}
          <div className="absolute top-0 right-0 w-full h-full overflow-hidden pointer-events-none">
            <div className={cn(
              "absolute -top-20 -right-20 w-64 h-64 bg-gradient-to-br from-[#d4a853]/10 to-transparent rotate-45 transition-all duration-1000 ease-out",
              isVisible ? "translate-x-0 opacity-100" : "translate-x-32 opacity-0"
            )} />
            <div className={cn(
              "absolute top-32 -right-32 w-80 h-80 bg-gradient-to-br from-emerald-500/5 to-transparent rotate-45 transition-all duration-1000 ease-out",
              isVisible ? "translate-x-0 opacity-100" : "translate-x-32 opacity-0"
            )} style={{ transitionDelay: '100ms' }} />
          </div>

          {/* Close button */}
          {onClose && (
            <button
              onClick={onClose}
              className="absolute top-6 right-6 z-20 p-2 rounded-full bg-[#1c2128] hover:bg-[#30363d] transition-all duration-300 group"
            >
              <X className="w-4 h-4 text-[#8b949e] group-hover:text-[#e6edf3]" />
            </button>
          )}

          <div className="relative z-10 p-10">

            {/* Hero headline section */}
            <div className={cn(
              "mb-8 transition-all duration-700 ease-out",
              isVisible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-8"
            )}>
              <div className="flex items-center gap-3 mb-4">
                <div className="h-1 w-12 bg-gradient-to-r from-[#d4a853] to-emerald-500 rounded-full" />
                <span className="text-xs font-bold tracking-widest text-[#d4a853] uppercase">
                  AI-Powered
                </span>
              </div>

              <h1 className="text-5xl font-bold leading-[1.1] text-[#e6edf3] mb-4" style={{
                fontFamily: 'ui-serif, Georgia, serif',
                letterSpacing: '-0.03em'
              }}>
                Your Watchlist,
                <br />
                <span className="bg-gradient-to-r from-[#d4a853] via-emerald-400 to-[#d4a853] bg-clip-text text-transparent animate-gradient bg-[length:200%_100%]">
                  One Snap Away
                </span>
              </h1>

              <p className="text-[#8b949e] text-base leading-relaxed max-w-md">
                Upload a portfolio screenshot or capture it live. Our AI extracts every ticker instantly—no manual entry needed.
              </p>
            </div>

            {/* Content */}
            {renderInnerContent()}
          </div>

          <style jsx global>{`
            @keyframes gradient {
              0%, 100% { background-position: 0% 50%; }
              50% { background-position: 100% 50%; }
            }
            .animate-gradient {
              animation: gradient 3s ease infinite;
            }
          `}</style>
        </div>
      </DialogContent>
    )
  }

  // Compact view
  if (isCompactView) {
    return (
      <div className={cn("w-full", className)}>
        {renderUploadOptions()}
        {renderExtractionStatus()}
        {extractedTickers.length > 0 && (
          <div className="mt-4">
            {renderTickerSelection()}
            {renderListNameInput()}
            {renderConfirmButton()}
          </div>
        )}
      </div>
    );
  }

  // Embedded mode (Card)
  return (
    <Card className={cn(
      "w-full border-[#30363d]",
      "md:max-w-2xl md:mx-auto",
      "bg-[#0d1117] shadow-xl",
      className
    )}>
      <CardHeader>
        <CardTitle className="text-xl font-semibold text-[#e6edf3]">Add Tickers via Image</CardTitle>
        <CardDescription className="text-sm text-[#8b949e]">
          Upload or snap a photo to extract tickers.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6 pt-6">
        {renderInnerContent()}
      </CardContent>
    </Card>
  )
}
